import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from "@simonbackx/simple-encoding";
import { ChoicesFilterChoice, ChoicesFilterDefinition, ChoicesFilterMode } from "../filters/ChoicesFilter";
import { NumberFilterDefinition } from "../filters/NumberFilter";
import { PropertyFilter } from "../filters/PropertyFilter";
import { RegistrationsFilterChoice, RegistrationsFilterDefinition } from "../filters/RegistrationsFilter";
import { Gender } from "./Gender";
import { LegacyRecordType } from "./records/LegacyRecordType";
import { RecordCategory } from "./records/RecordCategory";
export var AskRequirement;
(function (AskRequirement) {
    AskRequirement["NotAsked"] = "NotAsked";
    AskRequirement["Optional"] = "Optional";
    AskRequirement["Required"] = "Required";
})(AskRequirement || (AskRequirement = {}));
export class FreeContributionSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.description = "";
        this.amounts = [500, 1500, 3000];
    }
}
__decorate([
    field({ decoder: StringDecoder })
], FreeContributionSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(IntegerDecoder) })
], FreeContributionSettings.prototype, "amounts", void 0);
export class FinancialSupportSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * E.g. 'financial support'
         */
        this.title = FinancialSupportSettings.defaultTitle;
        /**
         * E.g. 'We provide financial support for families in financial difficulties. You can ask for this by checking this checkbox'
         */
        this.description = FinancialSupportSettings.defaultDescription;
        /**
         * E.g. 'My family is in need of financial support'
         */
        this.checkboxLabel = FinancialSupportSettings.defaultCheckboxLabel;
        /**
         * E.g. 'Uses financial support'
         */
        this.warningText = FinancialSupportSettings.defaultWarningText;
    }
    static get defaultDescription() {
        return "We doen ons best om de kostprijs van onze activiteiten zo laag mogelijk te houden. Daarnaast voorzien we middelen om gezinnen die dat nodig hebben te ondersteunen. Om de drempel zo laag mogelijk te houden, voorzien we een discrete checkbox waarmee je kan aangeven dat je ondersteuning nodig hebt. We gaan hier uiterst discreet mee om.";
    }
    static get defaultTitle() {
        return "Financiële ondersteuning";
    }
    static get defaultCheckboxLabel() {
        return "Mijn gezin heeft nood aan financiële ondersteuning en ik wil dit discreet kenbaar maken";
    }
    static get defaultWarningText() {
        return "Gebruikt financiële ondersteuning";
    }
}
__decorate([
    field({ decoder: StringDecoder })
], FinancialSupportSettings.prototype, "title", void 0);
__decorate([
    field({ decoder: StringDecoder })
], FinancialSupportSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: StringDecoder })
], FinancialSupportSettings.prototype, "checkboxLabel", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], FinancialSupportSettings.prototype, "warningText", void 0);
export class DataPermissionsSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * E.g. 'financial support'
         */
        this.title = DataPermissionsSettings.defaultTitle;
        /**
         * E.g. 'We provide financial support for families in financial difficulties. You can ask for this by checking this checkbox'
         */
        this.description = DataPermissionsSettings.defaultDescription;
        /**
         * E.g. 'My family is in need of financial support'
         */
        this.checkboxLabel = DataPermissionsSettings.defaultCheckboxLabel;
        /**
         * E.g. 'Uses financial support'
         */
        this.warningText = DataPermissionsSettings.defaultWarningText;
    }
    static get defaultDescription() {
        return "";
    }
    static get defaultTitle() {
        return "Toestemming verzamelen gevoelige gegevens";
    }
    static get defaultCheckboxLabel() {
        return "Ik geef toestemming om gevoelige gegevens te verzamelen en te verwerken. Hoe we met deze gegevens omgaan staat vermeld in ons privacybeleid.";
    }
    static get defaultWarningText() {
        return "Geen toestemming om gevoelige gegevens te verzamelen";
    }
}
__decorate([
    field({ decoder: StringDecoder })
], DataPermissionsSettings.prototype, "title", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DataPermissionsSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DataPermissionsSettings.prototype, "checkboxLabel", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], DataPermissionsSettings.prototype, "warningText", void 0);
/**
 * Convenicence class used to pass around details and current or future groups it is registered in
 * -> needed for filtering.
 */
export class MemberDetailsWithGroups {
    constructor(details, member, registerItems = []) {
        this.registerItems = [];
        this.details = details;
        this.member = member;
        this.registerItems = registerItems;
    }
    static getBaseFilterDefinitions() {
        return [
            // TODO: map member filters instead of redefining them
            new NumberFilterDefinition({
                id: "member_age",
                name: "Leeftijd",
                getValue: (member) => {
                    var _a;
                    return (_a = member.details.age) !== null && _a !== void 0 ? _a : 99;
                },
                floatingPoint: false
            }),
            new ChoicesFilterDefinition({
                id: "member_gender",
                name: "Geslacht",
                choices: [
                    new ChoicesFilterChoice(Gender.Male, "Man"),
                    new ChoicesFilterChoice(Gender.Female, "Vrouw"),
                    new ChoicesFilterChoice(Gender.Other, "Andere"),
                ],
                getValue: (member) => {
                    return [member.details.gender];
                },
                defaultMode: ChoicesFilterMode.Or
            }),
            new RegistrationsFilterDefinition({
                id: "registrations",
                name: "Inschrijvingen",
                getValue: (member) => {
                    var _a, _b, _c, _d, _e;
                    const groups = (_b = (_a = member.member) === null || _a === void 0 ? void 0 : _a.groups.map(g => RegistrationsFilterChoice.create({
                        id: g.id,
                        name: g.settings.name,
                        waitingList: false
                    }))) !== null && _b !== void 0 ? _b : [];
                    const waitingGroups = (_d = (_c = member.member) === null || _c === void 0 ? void 0 : _c.waitingGroups.map(g => RegistrationsFilterChoice.create({
                        id: g.id,
                        name: g.settings.name,
                        waitingList: true
                    }))) !== null && _d !== void 0 ? _d : [];
                    const pendingGroups = (_e = member.registerItems.map(item => RegistrationsFilterChoice.create({
                        id: item.group.id,
                        name: item.group.settings.name,
                        waitingList: item.waitingList
                    }))) !== null && _e !== void 0 ? _e : [];
                    return [...groups, ...waitingGroups, ...pendingGroups];
                }
            }),
            new ChoicesFilterDefinition({
                id: "member_missing_data",
                name: "Ontbrekende gegevens",
                description: "Als één van de geselecteerde gegevens ontbreekt of niet is ingevuld. Op deze manier kan je handige combinaties vormen, bv. een noodcontactpersoon enkel vragen als er maar één ouder is.",
                choices: [
                    new ChoicesFilterChoice("birthDay", "Geboortedatum"),
                    new ChoicesFilterChoice("address", "Adres", "Van lid zelf"),
                    new ChoicesFilterChoice("phone", "Telefoonnummer", "Van lid zelf"),
                    new ChoicesFilterChoice("email", "E-mailadres", "Van lid zelf"),
                    new ChoicesFilterChoice("parents", "Ouders"),
                    new ChoicesFilterChoice("secondParent", "Tweede ouder", "Als er maar één ouder is toegevoegd aan een lid. Handig om te selecteren op een eenoudergezin, om zo een extra contactpersoon mogelijk te maken"),
                    new ChoicesFilterChoice("emergencyContact", "Noodcontact"),
                ],
                explainFilter: (filter) => {
                    return "gegevens ontbreken (" + filter.choiceIds.map(id => { var _a, _b; return (_b = (_a = filter.definition.choices.find(c => c.id === id)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "?"; }).join(", ") + ")";
                },
                getValue: (member) => {
                    const missing = [];
                    if (!member.details.birthDay) {
                        missing.push("birthDay");
                    }
                    if (!member.details.address) {
                        missing.push("address");
                    }
                    if (!member.details.phone) {
                        missing.push("phone");
                    }
                    if (!member.details.email) {
                        missing.push("email");
                    }
                    if (member.details.parents.length == 0) {
                        missing.push("parents");
                    }
                    if (member.details.parents.length == 1) {
                        missing.push("secondParent");
                    }
                    if (member.details.emergencyContacts.length == 0) {
                        missing.push("emergencyContact");
                    }
                    return missing;
                },
                defaultMode: ChoicesFilterMode.Or
            }),
        ];
    }
    static getFilterDefinitions(organization, options) {
        var _a, _b, _c, _d, _e, _f, _g;
        // Make a list of all the groups
        const groups = (_a = options.groups) !== null && _a !== void 0 ? _a : [];
        groups.push(...(_c = (_b = options.member) === null || _b === void 0 ? void 0 : _b.groups) !== null && _c !== void 0 ? _c : []);
        groups.push(...(_e = (_d = options.member) === null || _d === void 0 ? void 0 : _d.waitingGroups) !== null && _e !== void 0 ? _e : []);
        groups.push(...(_g = (_f = options.registerItems) === null || _f === void 0 ? void 0 : _f.map(i => i.group)) !== null && _g !== void 0 ? _g : []);
        // Remove duplicates
        const uniqueGroups = groups.filter((group, index, self) => self.findIndex(g => g.id === group.id) === index);
        // Map groups to record categories that are relevant to them
        // TODO: we should move this from the organization settings to the group and category settings
        const recordCategories = organization.meta.recordsConfiguration.recordCategories;
        return [
            ...this.getBaseFilterDefinitions(),
            ...RecordCategory.getRecordCategoryDefinitions(recordCategories, (member) => {
                return member.details.recordAnswers;
            }),
        ];
    }
}
export class OrganizationRecordsConfiguration extends AutoEncoder {
    constructor() {
        // New record configurations
        super(...arguments);
        /**
         * If the organizations provides support for families in financial difficulties
         */
        this.financialSupport = null;
        /**
         * Ask permissions to collect data
         */
        this.dataPermission = null;
        this.emailAddress = null;
        this.phone = null;
        this.gender = null;
        this.birthDay = null;
        this.address = null;
        this.parents = null;
        this.emergencyContacts = null;
        this.recordCategories = [];
        // General configurations
        this.freeContribution = null;
        /**
         * @deprecated
         * Moved to recordCategories
         */
        this.enabledLegacyRecords = [];
        /**
         * @deprecated
         * true: required
         * false: don't ask
         * null: optional
         */
        this.doctor = AskRequirement.NotAsked;
        /**
         * @deprecated
         * true: required
         * false: don't ask
         * null: optional
         */
        this.emergencyContact = AskRequirement.Optional;
    }
}
__decorate([
    field({ decoder: FinancialSupportSettings, nullable: true, version: 117 })
], OrganizationRecordsConfiguration.prototype, "financialSupport", void 0);
__decorate([
    field({ decoder: DataPermissionsSettings, nullable: true, version: 117 })
], OrganizationRecordsConfiguration.prototype, "dataPermission", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 124 })
], OrganizationRecordsConfiguration.prototype, "emailAddress", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "phone", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "gender", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "birthDay", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "address", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "parents", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "emergencyContacts", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory), version: 117 })
], OrganizationRecordsConfiguration.prototype, "recordCategories", void 0);
__decorate([
    field({ decoder: FreeContributionSettings, nullable: true, version: 92 })
], OrganizationRecordsConfiguration.prototype, "freeContribution", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), field: "enabledRecords" }),
    field({ decoder: new ArrayDecoder(new EnumDecoder(LegacyRecordType)), upgrade: () => [], version: 55, field: "enabledRecords" }),
    field({ decoder: new ArrayDecoder(new EnumDecoder(LegacyRecordType)), version: 117, field: "enabledLegacyRecords" })
], OrganizationRecordsConfiguration.prototype, "enabledLegacyRecords", void 0);
__decorate([
    field({ decoder: new EnumDecoder(AskRequirement), optional: true })
], OrganizationRecordsConfiguration.prototype, "doctor", void 0);
__decorate([
    field({ decoder: new EnumDecoder(AskRequirement), optional: true })
], OrganizationRecordsConfiguration.prototype, "emergencyContact", void 0);
//# sourceMappingURL=OrganizationRecordsConfiguration.js.map